import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, IconButton, CircularProgress,
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Select, MenuItem, FormControl, InputLabel, Pagination
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import apiUrl from '../../Config';
import { Plus } from 'lucide-react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const DisplayCategory = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("")

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    // const [search, setSearch] = useState('');

    useEffect(() => {
        const fetchCategories = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${apiUrl}/categories/getallcategories?page=${page}&limit=${limit}`);

                setCategories(res.data.category || []);

                if (res.data.totalPages !== undefined) {
                    setTotalPages(res.data.totalPages);
                } else {
                    setTotalPages(1);
                }
                if (res.data.totalItems !== undefined) {
                    setTotalItems(res.data.totalItems);
                } else {
                    setTotalItems(res.data.category?.length || 0);
                }

            } catch (err) {
                console.log('Failed to fetch categories');
                const message = error?.response?.data?.message || error.message || 'Failed to fetch categories'
                setError(message)
            } finally {
                setLoading(false);
            }
        };
        fetchCategories();
    }, [page, limit,
        // search
    ]);

    const handleDelete = async (id) => {
        await axios.delete(`${apiUrl}/categories/deletecategories/${id}`);
        setCategories(categories.filter(cat => cat.id !== id));
        setTotalItems(prev => prev - 1);
    };

    const handleEditClick = (service) => {
        navigate('/create-edit-category', {
            state: {
                isEditMode: true,
                initialData: {
                    id: service.id || 0,
                    name: service.name,
                    description: service?.description,
                    categoriesImg: service?.imageUrl,
                    isActive: service?.isActive,
                    isPopular: service?.isPopular,
                    sequenceOrder: service?.sequenceOrder
                }
            }
        });
    };

    const handleAddClick = () => {
        navigate('/create-edit-category', {
            state: {
                isEditMode: false,
                initialData: {
                    id: '',
                    name: '',
                    description: '',
                    categoriesImg: null,
                    isActive: false,
                    isPopular: false,
                    sequenceOrder: ""
                }
            }
        });
    };

    const handleRowClick = (service) => {
        navigate(`/display-subcategory`, { state: { service } });
    };

    const handleLimitChange = (e) => {
        setLimit(Number(e.target.value));
        setPage(1);
    };

    return (
        <Box sx={{ backgroundColor: "primary", height: "100vh", padding: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h3">Categories</Typography>
                <Button
                    variant="contained"
                    sx={{ borderRadius: "25px", padding: "10px 20px", display: "flex", gap: 1 }}
                    onClick={handleAddClick}
                >
                    <Plus size={23} />
                    <Typography>Add Category</Typography>
                </Button>
            </Box>

            {/* Rows per page selector */}
            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Rows per page</InputLabel>
                    <Select value={limit} label="Rows per page" onChange={handleLimitChange}>
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                    </Select>
                </FormControl>

                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                    Total Categories: {totalItems}
                </Typography>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={1}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Image</strong></TableCell>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Description</strong></TableCell>
                                <TableCell><strong>Is Active</strong></TableCell>
                                <TableCell align='right'><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {categories.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No categories found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                categories.map((service) => (
                                    <TableRow key={service.id}>
                                        <TableCell>
                                            <img
                                                src={service.imageUrl}
                                                alt={service?.name}
                                                style={{ width: 70, height: 70, borderRadius: 5 }}
                                            />
                                        </TableCell>
                                        <TableCell>{service?.name}</TableCell>
                                        <TableCell>
                                            {service?.description?.length > 50
                                                ? service.description.slice(0, 50) + '...'
                                                : service.description}
                                        </TableCell>
                                        <TableCell>{service?.isActive === true ? "true" : "false"}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'end', gap: 1 }}>
                                                <IconButton onClick={() => handleEditClick(service)}>
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton color="error" onClick={() => handleDelete(service.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleRowClick(service)}
                                                >
                                                    View Subcategory
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Pagination */}
            <Box mt={2} display="flex" justifyContent="center" alignItems="center">
                <Pagination
                    count={totalPages > 0 ? totalPages : 1}
                    page={page > totalPages ? totalPages : page}
                    onChange={(event, value) => setPage(value)}
                    color="primary"
                    showFirstButton
                    showLastButton
                    disabled={totalPages === 0}
                    siblingCount={0}
                    boundaryCount={1}
                    sx={{
                        '& .MuiPaginationItem-root': {
                            height: 30,
                            minWidth: 30,
                            fontSize: '1rem',
                            marginTop: '10px'
                        }
                    }}
                />
            </Box>

            {error && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default DisplayCategory;
